import React, { useMemo, useState } from "react";
import { Cable, CheckCircle, Loop, SsidChart } from "@mui/icons-material";
import { Alert, AlertTitle, Button, Paper, Typography } from "@mui/material";

import { Progress } from "ui";

import { useCopyLayerFeatures } from "fond/hooks/useCopyLayerFeatures";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import useWorkflowErrorLogs from "fond/hooks/useWorkflowErrorLogs";
import { useProjectQuota } from "fond/project/solve/hooks/useProjectQuota";
import { LayerStatus, MuPowerOutputLayerIds, MuPowerSolverLayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { AccordionStepper, AccordionStepperPane, ConfirmModal, LinearProgressWithLabel, StackedNavigationButton, SvgIcon } from "fond/widgets";

import { useIsMuPowerDesignComplete } from "./useIsMuPowerDesignComplete";
import { useOverrides } from "./useOverrides";

const stageConfig: Record<string, { label: string }> = {
  power: { label: "Generating power design" },
  bom: { label: "Generating bill of materials" },
};

// The layers that need to be copied as prerequisites for using overrides
const lockPrereqsCopies = [
  { sourceKey: MuPowerOutputLayerIds.primaryWires, destKey: MuPowerSolverLayerIds.primaryFootprint, displayName: "Primary Footprint" },
  { sourceKey: MuPowerOutputLayerIds.secondaryWires, destKey: MuPowerSolverLayerIds.secondaryFootprint, displayName: "Secondary Footprint" },
  { sourceKey: MuPowerOutputLayerIds.transformers, destKey: MuPowerSolverLayerIds.transformerHubs, displayName: "Transformers" },
];

interface PowerSolverPanelProps {
  project: Project;
  isReadOnly?: boolean;
  canRun: boolean;
  solverLayers: Map<string, LayerStatus>;
}

const PowerSolverPanel: React.FC<PowerSolverPanelProps> = ({ project, isReadOnly, canRun, solverLayers }) => {
  const versionId = useAppSelector((state) => state.project.versionId);

  // Running the power solver requires an active planner subscription (but does not consume prems).
  const { quota, isLoaded: hasLoadedSubscriptions } = useProjectQuota(project);
  const hasPlannerSubscription = !!quota;

  const { workflowState, stages, runWorkflow, cancelWorkflow, logs } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.MU_POWER.POWER_SOLVER,
  });
  const { errorLogsInline, logsModal } = useWorkflowErrorLogs(logs);

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const isAnyUploading = Array.from(solverLayers.values()).some((layer) => isImportInProgress(layer.status));

  const hasSourceHubs = !!solverLayers.get(MuPowerSolverLayerIds.sourceHubs)?.layer;
  const hasBlockers =
    !!solverLayers.get(MuPowerSolverLayerIds.primaryBlockers)?.layer || !!solverLayers.get(MuPowerSolverLayerIds.secondaryBlockers)?.layer;

  const isDesignComplete = useIsMuPowerDesignComplete(versionId);

  const rightAdornment = (complete: boolean) => (complete ? <CheckCircle color="success" sx={{ fontSize: 18 }} /> : undefined);

  const showLayerButtons = !isReadOnly && !isInProgress;

  const {
    isOverridesSet,
    clearOverrides,
    hasFootprint,
    hasTransformers,
    hasThreePhasePath,
    hasLoopConnections,
    hasPhaseOverrides,
    hasOverridePrereqs,
  } = useOverrides(solverLayers);
  const [showClearOverridesConfirm, setShowClearOverridesConfirm] = useState(false);
  const [isClearingOverrides, setIsClearingOverrides] = useState(false);

  const handleConfirmClearOverrides = async () => {
    setIsClearingOverrides(true);
    try {
      await clearOverrides();
      setShowClearOverridesConfirm(false);
    } finally {
      setIsClearingOverrides(false);
    }
  };

  const {
    copyFeatures: lockPrereqs,
    isCopying: isLockingPrereqs,
    hasSourceFeatures: hasPrereqOutputs,
  } = useCopyLayerFeatures(versionId, lockPrereqsCopies, "Footprint and transformers");

  const designAccordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Optimize",
        id: "optimize",
        content: showLayerButtons ? (
          <>
            {isOverridesSet ? (
              <Alert severity="warning" variant="compact" sx={{ mb: 1 }}>
                <AlertTitle>Overrides applied</AlertTitle>These settings cannot be modified after overrides have been set.
              </Alert>
            ) : (
              <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
                <AlertTitle>Optimize locations and path</AlertTitle>
                <ul style={{ margin: 0, paddingLeft: "1rem" }}>
                  <li>Source hubs are required</li>
                  <li>Blockers are recommended</li>
                </ul>
              </Alert>
            )}
            <StackedNavigationButton
              icon={<SvgIcon icon="points" />}
              title="Source Hubs"
              target={MuPowerSolverLayerIds.sourceHubs}
              sx={{ m: 0, mt: 1 }}
              checked={hasSourceHubs}
              rightElement={rightAdornment(hasSourceHubs)}
              disabled={isOverridesSet}
            />
            <StackedNavigationButton
              icon={<SvgIcon icon="line" />}
              title="Blockers"
              target="mu-blockers"
              sx={{ m: 0, mt: 1 }}
              checked={hasBlockers}
              rightElement={rightAdornment(hasBlockers)}
              disabled={isOverridesSet}
            />
            <StackedNavigationButton
              icon={<SvgIcon icon="points" />}
              title="Transformers"
              target="mu-transformers"
              sx={{ m: 0, mt: 1 }}
              checked={hasTransformers}
              rightElement={rightAdornment(hasTransformers)}
              disabled={isOverridesSet}
            />
            {isOverridesSet && (
              <Button fullWidth variant="outlined" color="error" size="small" sx={{ mt: 1 }} onClick={() => setShowClearOverridesConfirm(true)}>
                Clear overrides
              </Button>
            )}
          </>
        ) : null,
        isComplete: hasSourceHubs,
      },
      {
        header: "Override",
        id: "override",
        content: showLayerButtons ? (
          <>
            {!isDesignComplete ? (
              <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
                <AlertTitle>Design required</AlertTitle>A design must be generated before overrides can be applied.
              </Alert>
            ) : !hasOverridePrereqs ? (
              <>
                <Alert severity="warning" variant="compact" sx={{ mb: 1 }}>
                  <AlertTitle>Setup required</AlertTitle>Footprint and transformers must be locked to use design overrides.
                </Alert>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                  disabled={!hasPrereqOutputs || isLockingPrereqs}
                  onClick={() => lockPrereqs()}
                >
                  {isLockingPrereqs ? "Locking..." : "Lock footprint and transformers"}
                </Button>
              </>
            ) : (
              <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
                <AlertTitle>Manual overrides</AlertTitle>Use overrides once locations and path are finalized.
              </Alert>
            )}
            <StackedNavigationButton
              icon={<SvgIcon icon="line" />}
              title="Footprint"
              target="mu-footprint"
              sx={{ m: 0, mt: 1 }}
              checked={hasFootprint}
              rightElement={rightAdornment(hasFootprint)}
              disabled={!isDesignComplete}
            />
            <StackedNavigationButton
              icon={<Cable />}
              title="3-phase path"
              target="mu-three-phase-path"
              sx={{ m: 0, mt: 1 }}
              iconSize="small"
              checked={hasThreePhasePath}
              rightElement={rightAdornment(hasThreePhasePath)}
              disabled={!hasOverridePrereqs}
            />
            <StackedNavigationButton
              icon={<Loop />}
              title="Loop connections"
              target="mu-loop-connections"
              sx={{ m: 0, mt: 1 }}
              iconSize="small"
              checked={hasLoopConnections}
              rightElement={rightAdornment(hasLoopConnections)}
              disabled={!hasOverridePrereqs}
            />
            <StackedNavigationButton
              icon={<SsidChart />}
              title="Phase overrides"
              target="mu-phase-overrides"
              sx={{ m: 0, mt: 1 }}
              iconSize="small"
              checked={hasPhaseOverrides}
              rightElement={rightAdornment(hasPhaseOverrides)}
              disabled={!hasOverridePrereqs}
            />
          </>
        ) : null,
        isComplete: isOverridesSet,
        optional: true,
      },
    ],
    [
      hasBlockers,
      hasFootprint,
      hasLoopConnections,
      hasOverridePrereqs,
      hasPhaseOverrides,
      hasPrereqOutputs,
      hasSourceHubs,
      hasThreePhasePath,
      hasTransformers,
      isDesignComplete,
      isLockingPrereqs,
      isOverridesSet,
      lockPrereqs,
      showLayerButtons,
    ]
  );

  return (
    <>
      {hasLoadedSubscriptions && !hasPlannerSubscription && (
        <Alert severity="warning" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Subscription required</AlertTitle>
          An active Planner subscription is required to generate a design.
        </Alert>
      )}

      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation required</AlertTitle>
          Please complete data preparation before running the power solver.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Power solver failed</AlertTitle>
          There was an error running the power solver. Please try again.
          {errorLogsInline}
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Power design complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {!isInProgress && <AccordionStepper id={`${versionId}-power-solver-design`} panes={designAccordionPanes} variant="outlined" />}

      {!isInProgress && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ my: 0 }}
          disabled={!runWorkflow || !canRun || !hasSourceHubs || !hasPlannerSubscription || isInProgress || isAnyUploading || isReadOnly}
          onClick={() => runWorkflow?.()}
        >
          {workflowState === "COMPLETE" ? "Re-run design generation" : "Start design generation"}
        </Button>
      )}

      {isInProgress && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ my: 2 }}
          disabled={!cancelWorkflow || !canRun || !isInProgress || isReadOnly}
          onClick={() => cancelWorkflow?.()}
        >
          Cancel
        </Button>
      )}

      {isAnyUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}

      <ConfirmModal
        open={showClearOverridesConfirm}
        header="Clear overrides?"
        confirmText="Clear"
        cancelText="Cancel"
        color="error"
        content={<Typography variant="body2">This will permanently delete all override layers. This action cannot be undone.</Typography>}
        onConfirm={handleConfirmClearOverrides}
        onCancel={() => setShowClearOverridesConfirm(false)}
        disabled={isClearingOverrides}
      />

      {logsModal}
    </>
  );
};

export default PowerSolverPanel;
